import { useState } from 'react';
import { useDispatch } from 'react-redux';

import InvestorSkipTraceModal from 'app/components/InvestorSkipTraceModal';
import { materializeInvestorContacts } from 'data/investors';
import { toInvestorContactRequest } from 'data/investors/investorUtils';

// Skip Trace on investors (ATAI-115 / S9). Shared by the search result card's
// own "Skip Trace" button (single investor) and the Results header's bulk
// "Actions -> Skip Trace" item.
//
// Opens InvestorSkipTraceModal, NOT the classic SkipTraceModal -- its "Name
// Your List" field only names the skip-trace job record, not a real Investor
// List (see InvestorSkipTraceModal's own comment for why this needed a
// separate component rather than a shared-file edit). The estimate call /
// "job already in progress" check lives in that modal itself, not here.
const useSkipTraceInvestors = () => {
  const dispatch = useDispatch();
  const [materializing, setMaterializing] = useState(false);

  // The modal fetches its own estimate (and shows the "job in progress"
  // alert) on every open, so no estimate call is needed here (ATAI-474).
  const openInvestorSkipTraceModal = (investors, contactIds, onSuccess, onClose) => {
    dispatch(InvestorSkipTraceModal.open({ investors, contactIds, onSuccess, onClose }));
  };

  // `onModalToggle`, when the caller is itself inside another already-open
  // popup (e.g. ContactInfo.js inside the Investor Profile modal): called
  // with `true` right before this opens anything and `false` once it's done
  // -- same contract as useSaveInvestor's `onToggle` / PropertyTable's
  // `onPropertyDetailToggle`. Covers the whole materialize -> estimate ->
  // modal chain, not just the modal itself, since the "existing append job"
  // InfoAlert and the estimate-error early return are also popups/no-ops
  // that need the outer modal's outside-click handling restored on exit.
  const skipTrace = (investors, { onSuccess, onModalToggle } = {}) => {
    if (!investors?.length || materializing) return;

    onModalToggle?.(true);
    setMaterializing(true);

    dispatch(
      materializeInvestorContacts(
        { investors: investors.map(toInvestorContactRequest) },
        ({ response }) => {
          setMaterializing(false);

          const ids = (response?.results || [])
            .map((result) => result.contactId)
            .filter((id) => id != null);

          if (!ids.length) {
            console.warn('Materialize returned no contact ids for skip trace');
            onModalToggle?.(false);
            return;
          }

          openInvestorSkipTraceModal(investors, ids, onSuccess, () => onModalToggle?.(false));
        },
        () => {
          setMaterializing(false);
          console.warn('Could not materialize investors for skip trace');
          onModalToggle?.(false);
        }
      )
    );
  };

  return { skipTrace, materializing };
};

export default useSkipTraceInvestors;
